import { HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { OrganizationLevel } from '../models/organizationLevel.model';
import { CommonService } from '../services/common.service';

export const CACHEABLE = new HttpContextToken<boolean>(() => false);
export const BYPASS_CACHE = new HttpContextToken<boolean>(() => false);

interface CacheEntry {
    observable$: Observable<HttpEvent<unknown>>;
    expiresAt: number;
}

const HTTP_METHOD_GET = 'GET';
const TTL_MS = 30_000;

const CACHEABLE_URL_PATTERNS: RegExp[] = [
    /\/OrganizationLevel\/GetAll(\?|$)/,
    /\/Parameter\/GetAll(\?|$)/,
    /\/User\/GetAllTimeZones(\?|$)/,
    /\/User\/GetAll(\?|$)/,
    /\/Role\/GetAll(\?|$)/,
    /\/AgentGroupOrgLevel\/GetAll(\?|$)/,
    /\/AgentOrgLevel\/GetAll(\?|$)/,
    /\/JurisdictionOrgLevel\/GetAll(\?|$)/,
    /\/Agent\/GetAll(\?|$)/,
    /\/Seg\/GetAll(\?|$)/,
    /\/AgentGroup\/GetAll(\?|$)/,
    /\/Jurisdiction\/GetAll(\?|$)/,
    /\/OelType\/GetAll(\?|$)/
];

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
    private readonly cache = new Map<string, CacheEntry>();
    private orgSubscribed = false;
    private lastOrgId: string | null = null;

    constructor(private readonly injector: Injector) {}

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        this.ensureOrgInvalidationWired();

        if (request.method !== HTTP_METHOD_GET) {
            this.invalidateForMutation(request);
            return next.handle(request);
        }

        if (request.context.get(BYPASS_CACHE)) {
            return next.handle(request);
        }

        if (!this.isCacheable(request)) {
            return next.handle(request);
        }

        const key = this.buildKey(request);
        const existing = this.cache.get(key);
        if (existing && existing.expiresAt > Date.now()) {
            return existing.observable$;
        }

        const shared$ = next.handle(request).pipe(
            tap({ error: () => this.cache.delete(key) }),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        this.cache.set(key, { observable$: shared$, expiresAt: Date.now() + TTL_MS });
        return shared$;
    }

    private ensureOrgInvalidationWired(): void {
        if (this.orgSubscribed) {
            return;
        }
        this.orgSubscribed = true;
        const commonService = this.injector.get(CommonService);
        commonService.selectedOrgEmitter.subscribe((org: OrganizationLevel | null) => {
            const newId = org?.id ?? null;
            if (newId !== this.lastOrgId) {
                this.cache.clear();
                this.lastOrgId = newId;
            }
        });
    }

    private isCacheable(request: HttpRequest<unknown>): boolean {
        if (request.context.get(CACHEABLE)) {
            return true;
        }
        return CACHEABLE_URL_PATTERNS.some(re => re.test(request.urlWithParams));
    }

    private buildKey(request: HttpRequest<unknown>): string {
        const lang = request.headers.get('Accept-Language') ?? '';
        return `${HTTP_METHOD_GET}|${request.urlWithParams}|${lang}`;
    }

    private invalidateForMutation(request: HttpRequest<unknown>): void {
        const controller = this.extractController(request.url);
        if (!controller) {
            return;
        }
        for (const key of this.cache.keys()) {
            if (key.includes(`/${controller}/`)) {
                this.cache.delete(key);
            }
        }
    }

    private extractController(url: string): string | null {
        const match = url.match(/\/api\/([^/?]+)/);
        return match ? match[1] : null;
    }
}
